import {FormEvent, ReactNode, useState} from "react";
import {Button, Field, HStack, Input} from "@chakra-ui/react";
import {captureException} from "@sentry/core";
import {errorDescription} from "platformed-client";
import T from "platformed-components/i18n/T";
import {usePromiseState} from "../../hooks/promiseState";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "../../components/ui/dialog";
import {Alert} from "../../components/ui/alert";

export type NameDialogProps = {
  title: ReactNode;
  submitLabel: ReactNode;
  /**
   * Receives the name trimmed, and only once it is non-blank. A rejection keeps
   * the dialog open on the error, with the name still typed in.
   */
  onSubmit: (name: string) => Promise<unknown>;
};

type Props = NameDialogProps & {open: boolean; onClose: () => void; onCloseComplete?: () => void};

/**
 * Asks for the name of something about to be created, which has no nameless
 * form. Every way out is shut while the create is in flight, since dismissing
 * then would not stop it.
 */
export const NameDialog = ({open, onClose, onCloseComplete, ...props}: Props) => {
  const [submitting, setSubmitting] = useState(false);

  return (
    <DialogRoot
      open={open}
      onOpenChange={e => !e.open && !submitting && onClose()}
      closeOnEscape={!submitting}
      closeOnInteractOutside={!submitting}
      // Each opening starts from an empty name.
      lazyMount
      unmountOnExit
      onExitComplete={onCloseComplete}
      preventScroll={false}
    >
      <DialogContent>
        <NameForm {...props} onClose={onClose} onSubmitting={setSubmitting} />
        {!submitting && <DialogCloseTrigger />}
      </DialogContent>
    </DialogRoot>
  );
};

const NameForm = ({
  title,
  submitLabel,
  onSubmit,
  onClose,
  onSubmitting,
}: NameDialogProps & {onClose: () => void; onSubmitting: (submitting: boolean) => void}) => {
  const [name, setName] = useState("");
  const trimmed = name.trim();

  const [submitting, submit] = usePromiseState(
    async (e: FormEvent) => {
      e.preventDefault();
      if (trimmed === "") return;
      onSubmitting(true);
      try {
        await onSubmit(trimmed);
      } catch (err) {
        captureException(err);
        throw err;
      } finally {
        onSubmitting(false);
      }
      onClose();
    },
    [trimmed, onSubmit, onClose, onSubmitting],
  );

  return (
    <form onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <Field.Root required>
          <Field.Label>
            <T keyName="c.Name" />
          </Field.Label>
          <Input value={name} onChange={e => setName(e.target.value)} maxLength={256} autoFocus />
        </Field.Root>
        {submitting.lastError != null && (
          <Alert status="error" mt={4} title={<T keyName="c.An_error_occurred" />}>
            {errorDescription(submitting.lastError)}
          </Alert>
        )}
      </DialogBody>
      <DialogFooter>
        <HStack gap="3">
          <Button variant="outline" onClick={onClose} disabled={submitting.inProgress}>
            <T keyName="c.Cancel" />
          </Button>
          <Button
            colorPalette="blue"
            type="submit"
            disabled={trimmed === "" || submitting.inProgress}
            loading={submitting.inProgress}
          >
            {submitLabel}
          </Button>
        </HStack>
      </DialogFooter>
    </form>
  );
};
